/**
 * Statistics Folder Loader - Loads statistics for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  StatisticsFolderNode,
  createStatisticNode,
  StatisticNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getTableStatistics, StatisticInfo, formatStatisticDisplayName } from './statisticsService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadStatisticsFolderOptions {
  node: StatisticsFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the statistics for a table when the Statistics folder is expanded
 */
export async function loadStatisticsFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadStatisticsFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'statistics-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-statistics`,
        parentId: node.id,
        name: 'Loading statistics...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getTableStatistics(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'statistics-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.unsupported) {
      updateNode(node.id, {
        children: [
          {
            id: `unsupported-${connectionId}-${database}-${schema}-${tableName}-statistics`,
            parentId: node.id,
            name: 'Statistics not available for this engine',
            type: 'message',
            canHaveChildren: false,
            children: [],
            isExpanded: false,
            icon: 'message',
            metadata: { connectionId, database },
          } as MessageTreeNode,
        ],
        isLoading: false,
      });
      return;
    }

    if (result.statistics.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'statistics-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const statisticNodes = result.statistics.map((stat: StatisticInfo) => {
      const displayName = formatStatisticDisplayName(stat);
      const metadata: StatisticNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        statisticName: stat.name,
        columns: stat.columns,
        autoCreated: stat.autoCreated,
        userCreated: stat.userCreated,
        lastUpdated: stat.lastUpdated,
      };
      const statisticNode = createStatisticNode(metadata, node.id);
      statisticNode.name = displayName;
      return statisticNode;
    });

    updateNode(node.id, {
      children: statisticNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load statistics';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'statistics-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
